import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { folioService } from '../../services/folioService';
import CompanyPicker, { defaultTicker } from './folio/CompanyPicker';
import FolioModel from './folio/FolioModel';
import ReviewQueue from './folio/ReviewQueue';
import FundamentalsReviewPage from './FundamentalsReviewPage';

// Folio — the fundamentals module (superuser only; the backend gates every call). Two tabs in v1:
//   Model  — the company's statements as the current model serves them (Glide grid, added rows from a fixed menu)
//   Review — the review page (questions by pipeline stage, history of how rows continued) for the same company,
//            or — toggle "All companies (N)" / ?scope=all — the CROSS-COMPANY queue of every question that needs the
//            owner (ReviewQueue, 2026-10-26); a queue row opens its question (?ticker=T&q=<id>, highlighted and
//            scrolled to on that company's review). The landing tile opens the queue.
// One company picker on top of both; ?ticker=, ?tab=, ?scope= and ?q= keep the address shareable.
// Plan: Plans/analyst_system/Folio_Plan.md.

const TABS = [{ key: 'model', label: 'Model' }, { key: 'review', label: 'Review' }];

export default function FolioPage() {
  const [params, setParams] = useSearchParams();
  const urlTicker = (params.get('ticker') || '').toUpperCase();
  const tab = params.get('tab') === 'review' ? 'review' : 'model';
  const scope = tab === 'review' && params.get('scope') === 'all' ? 'all' : 'company';
  const focusId = params.get('q') || '';
  const [companies, setCompanies] = useState([]);
  const [pending, setPending] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState('');
  const [units, setUnits] = useState(null);      // the units the Model grid shows ('m' | 'b'), reported by FolioModel

  const loadCompanies = useCallback(() => folioService.getCompanies()
    .then((r) => { setCompanies(r.data.companies || []); setPending(r.data.pending || []); })
    .catch(() => setErr('Could not load the companies.'))
    .finally(() => setLoaded(true)), []);
  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setParams(next, { replace: true });
  };
  const allScope = tab === 'review' && scope === 'all';
  // the cross-company queue picks no company of its own: without ?ticker= the picker reads "Choose a company" (it
  // used to show the default company — one the reader never chose — with its count)
  const ticker = urlTicker || (allScope ? '' : defaultTicker(companies));
  const company = companies.find((c) => c.ticker === ticker);
  // the same rows the queue lists (the picker's counts and the queue come from one reader on the backend)
  const needsAll = companies.reduce((n, c) => n + (c.needs_you || 0), 0);
  // the Review tab counts what the page under it lists: this company's needs-you, or every company's in the queue
  const reviewCount = allScope ? needsAll : (company?.needs_you || 0);
  const segBtn = (on) => `min-h-[44px] px-2.5 py-1.5 sm:px-3 text-xs uppercase tracking-wider focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-gold/60 sm:min-h-0 ${on
    ? 'bg-brand-gold/10 text-brand-gold' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`;

  return (
    // `folio-accent`: Folio keeps the house gold in the light theme too (index.css; the platform's light accent is cobalt)
    <div className="folio-accent space-y-4">
      {/* picker and tabs share ONE row at every width (a phone stacked them: ~50 px of chrome above the grid) */}
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="min-w-0 flex-1 sm:flex-none">
          <CompanyPicker companies={companies} pending={pending} value={ticker}
            onChange={(t) => set({ ticker: t, q: '', scope: '' })} onAdded={() => { loadCompanies(); }} />
        </div>
        <nav className="flex flex-none gap-0.5 sm:ml-auto sm:gap-1" role="tablist" aria-label="Folio">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
              onClick={() => set({ tab: t.key === 'model' ? '' : t.key, ticker, q: '', scope: t.key === 'review' ? params.get('scope') : '' })}
              className={`rounded-lg ${segBtn(tab === t.key)}`}>
              {t.label}{t.key === 'review' && reviewCount ? ` · ${reviewCount}` : ''}
            </button>
          ))}
        </nav>
      </div>
      {tab === 'review' && loaded && (
        <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800" role="group" aria-label="Review scope">
          <button type="button" aria-pressed={!allScope} onClick={() => set({ scope: '', ticker })} className={segBtn(!allScope)}>
            This company{!allScope && company?.needs_you ? ` (${company.needs_you})` : ''}
          </button>
          <button type="button" aria-pressed={allScope} onClick={() => set({ scope: 'all', q: '', ticker })}
            className={`border-l border-gray-200 dark:border-gray-800 ${segBtn(allScope)}`}>
            All companies ({needsAll})
          </button>
        </div>
      )}
      {allScope ? (
        <div>
          <h1 className="font-serif text-2xl text-gray-900 dark:text-gray-100">Needs you — all companies</h1>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {needsAll} question{needsAll === 1 ? '' : 's'} · {companies.length} compan{companies.length === 1 ? 'y' : 'ies'} covered
          </div>
        </div>
      ) : company && (
        <div>
          {/* on a phone's Model tab the picker already names the company: the heading would only push the grid down */}
          <h1 title={company.sec_name || undefined} className={`font-serif text-2xl text-gray-900 dark:text-gray-100 ${tab === 'model' ? 'hidden sm:block' : ''}`}>{company.name}</h1>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {company.ticker} · {tab === 'model' ? `current model${units ? ` · USD ${units === 'b' ? 'billions' : 'millions'}, shares in millions, per share in USD` : ''}` : 'review'}{company.loading ? ' · still loading' : ''}</div>
        </div>
      )}
      {err && <div className="text-sm text-red-600 dark:text-red-400">{err}</div>}
      {!loaded && <div className="text-sm text-gray-500">Loading the companies…</div>}
      {loaded && !allScope && !ticker && !err && <div className="text-sm text-gray-500">No company is covered yet — add one from the picker.</div>}
      {loaded && !allScope && ticker && !company && !err && (
        <div className="text-sm text-gray-500">{ticker} is not covered{pending.includes(ticker) ? ' yet — it is being added' : ''}.</div>
      )}
      {allScope && loaded && <ReviewQueue onOpen={(t, id) => set({ tab: 'review', ticker: t, q: String(id), scope: '' })} />}
      {!allScope && company && tab === 'model' && <FolioModel ticker={ticker} onUnits={setUnits}
        onOpenQuestion={(q) => set({ tab: 'review', ticker, q: q ? String(q) : '', scope: '' })} />}
      {!allScope && company && tab === 'review' && (
        <FundamentalsReviewPage ticker={ticker} focusId={focusId} onChanged={loadCompanies} />
      )}
    </div>
  );
}
